import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * "Primeira vista" — the front elevation, shown as an EDGE-TO-EDGE
 * cinematic band. Source photos are wide (front.jpg ≈ 3.62:1, back.jpg
 * ≈ 2.92:1). Forcing them into a max-w container meant black rails on
 * both sides (screen-shot-reviewed 2026-04-24). Fix is two-layer:
 *
 *   1. The photo band spans 100vw — it bleeds all the way to the edge,
 *      no container, no black bars.
 *   2. Aspect ratio of the band matches the source image so the browser
 *      downscales width and the image sets its own height instead of a
 *      fixed 16/7 crop. `object-cover` + `object-center` cover the rest.
 *
 * Editorial text still sits INSIDE a max-w container above the image —
 * the ragged edge on the image is the point; the type on the text isn't.
 */
export function HouseFront() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Ken-Burns drift inside the band. Frame holds still; image shifts.
  const imgX = useTransform(scrollYProgress, [0, 1], ['-2.5%', '2.5%']);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);

  const textY = useTransform(scrollYProgress, [0.05, 0.35], [40, 0]);
  const textO = useTransform(scrollYProgress, [0.05, 0.3], [0, 1]);

  return (
    <section
      ref={ref}
      className="relative border-t border-line bg-bg py-24 sm:py-32"
      aria-label="Frente da casa"
    >
      {/* Editorial intro sits inside a container so measure stays ≤ 52ch. */}
      <motion.div
        className="mx-auto mb-12 max-w-7xl px-6 sm:mb-16 sm:px-12 lg:px-20"
        style={{ opacity: textO, y: textY }}
      >
        <div className="max-w-3xl">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Primeira vista
          </p>
          <h2 className="mt-5 font-display text-[clamp(2rem,5vw,3rem)] font-light leading-[1.05] tracking-tighter text-ink [text-wrap:balance]">
            Madeira, vidro, silêncio do fim da tarde.
          </h2>
          <p className="mt-5 max-w-[52ch] text-sm leading-relaxed text-ink-muted sm:text-base">
            Lâminas verticais de madeira protegem uma parede de vidro
            contínua. O telhado avança em beiral longo, quase flutuante.
            Sem pórtico cenográfico — a entrada é um corte discreto no
            volume.
          </p>
        </div>
      </motion.div>

      {/* Full-bleed band. The source front.jpg is 1600x442 (≈ 3.62:1);
          we honor that aspect-ratio so the image fills 100vw without
          vertical cropping on wide monitors or excessive letterbox on
          narrow phones. */}
      <motion.div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: '1600 / 442' }}
        initial={{ opacity: 0.6 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-15%' }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="absolute inset-0 will-change-transform"
          style={{ x: imgX, scale: imgScale, transform: 'translateZ(0)' }}
        >
          <HousePhoto
            src="/genese/front.jpg"
            alt="Fachada frontal de Gênese ao entardecer"
            placeholderTone="warm"
            // Wave 299c · responsive variants generated at build time
            // (apps/casa/public/genese/front-{800,1200}w.jpg). The
            // browser picks the smallest that covers its viewport,
            // dropping ~80% of bytes on phones while keeping the
            // 1600w master for retina monitors.
            widths={[800, 1200, 1600]}
            sizes="100vw"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}

// ───────────────────────────── helpers ─────────────────────────────

/**
 * Wave 299c — `widths` lets a caller opt into responsive variants
 * generated at build time. We expect a sibling for each width following
 * the convention `<base>-<W>w.<ext>` (e.g. `/genese/front-800w.jpg`),
 * with the original `src` as the largest fallback. Pass an empty array
 * (the default) to skip srcset and ship the single source — useful for
 * dynamic R2 photos that don't have variants pre-generated.
 *
 * `eager` flips loading=eager + fetchpriority=high for the very-first-
 * paint hero shot. Everything else stays lazy.
 */
export function HousePhoto({
  src,
  alt,
  placeholderTone,
  widths = [],
  sizes = '100vw',
  eager = false,
}: {
  src: string;
  alt: string;
  placeholderTone: 'warm' | 'cool';
  widths?: number[];
  sizes?: string;
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return <PhotoPlaceholder tone={placeholderTone} />;
  // Build the srcset by deriving sibling URLs for each width and
  // appending the original at its native max width. Skipping srcset
  // when `widths` is empty avoids breaking dynamic / R2-served photos
  // that don't have sibling variants.
  const srcSet =
    widths.length > 0
      ? [
          ...widths.map((w) => `${src.replace(/\.(jpg|jpeg|png|webp)$/i, `-${w}w.$1`)} ${w}w`),
        ].join(', ')
      : undefined;
  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      decoding="async"
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : 'auto'}
      onError={() => setFailed(true)}
      className="absolute inset-0 h-full w-full object-cover object-center"
    />
  );
}

function PhotoPlaceholder({ tone }: { tone: 'warm' | 'cool' }) {
  const gradient =
    tone === 'warm'
      ? 'radial-gradient(ellipse at 70% 60%, rgba(212,184,150,0.18), transparent 65%), linear-gradient(135deg, #0b0b0b 0%, #141210 100%)'
      : 'radial-gradient(ellipse at 40% 40%, rgba(130,180,210,0.15), transparent 65%), linear-gradient(135deg, #0a0c0d 0%, #111418 100%)';
  return (
    <div aria-hidden className="absolute inset-0" style={{ background: gradient }}>
      <svg viewBox="0 0 1200 500" className="absolute inset-0 h-full w-full opacity-60" preserveAspectRatio="xMidYMid slice">
        <path
          d="M0 360 L300 360 L340 290 L780 290 L820 360 L1200 360 L1200 440 L0 440 Z"
          fill="#f5f5f4"
          fillOpacity="0.04"
          stroke="#f5f5f4"
          strokeOpacity="0.18"
          strokeWidth="1"
        />
        <line x1="0" y1="440" x2="1200" y2="440" stroke="#f5f5f4" strokeOpacity="0.22" />
      </svg>
    </div>
  );
}
